import type { ColumnSort, SortingState } from '@tanstack/react-table';
import { ObjectParam, withDefault } from 'use-query-params';
import { systemError } from 'feature-components/system-error-alert';

export const defaultSortingFilter = withDefault(ObjectParam, {});

/**
 * The format of the "sorting" filter object used by useQueryParams.
 */
type SortingFilter = { [key: string]: string | undefined };

/**
 * Converts an object param value (from useQueryParams ObjectParam) to a SortingState that can be
 * used to set the initial sorting by TanStack Table.
 */
export function toSortingState(sortingFilter: SortingFilter): SortingState {
  return Object.entries(sortingFilter)
    .filter(([, value]) => value === 'desc' || value === 'asc')
    .map(([key, value]): ColumnSort => ({ id: key, desc: value === 'desc' }));
}

/**
 * Converts a SortingState object used by TanStack table back into a ObjectParam value that can
 * be set by useQueryParams.
 */
export function fromSortingState(sorting: SortingState): SortingFilter {
  return sorting.reduce(
    (accumulator, columnSort) => ({
      ...accumulator,
      [columnSort.id]: columnSort.desc ? 'desc' : 'asc'
    }),
    {}
  );
}

/**
 * Returns true if the column name is included in the sorting filter object.
 */
export function isColumnSorted(columnName: string, sorting: SortingFilter): boolean {
  return Object.keys(sorting).includes(columnName);
}

/**
 * Returns "asc" or "desc" for the column name included in the sorting filter object.
 */
export function sortDirection(columnName: string, sorting: SortingFilter): string {
  return sorting[columnName] || systemError('A direction other than asc or desc was specified');
}
